import { notesApi } from '@/core/api/endpoints/notes';

export const MAX_COMPOSER_IMAGES = 9;
export const COMPOSER_IMAGE_ACCEPT = 'image/png,image/jpeg,image/gif,image/webp';

export type ComposerUploadStage =
  | 'pending'
  | 'requesting_upload_url'
  | 'uploading'
  | 'confirming'
  | 'uploaded'
  | 'failed';

export interface ComposerUploadSnapshot {
  stage: ComposerUploadStage;
  stageProgress: number;
  overallProgress: number;
}

export interface ComposerUploadMetrics {
  totalMs: number;
  getUploadUrlMs: number;
  uploadOssMs: number;
  confirmUploadMs: number;
}

export interface UploadedComposerImage {
  fileUrl: string;
  objectKey?: string;
  etag?: string;
  fileSize?: number;
  mimeType?: string;
  metrics: ComposerUploadMetrics;
}

export async function uploadDetachedComposerImage(
  file: File,
  onUpdate: (snapshot: ComposerUploadSnapshot) => void = () => undefined
): Promise<UploadedComposerImage> {
  const fileName = file.name || `image-${Date.now()}.jpg`;
  const contentType = normalizeMimeType(file);
  const startedAt = Date.now();

  const getUploadStartedAt = Date.now();
  onUpdate(makeSnapshot('requesting_upload_url', 0));
  const presigned = await notesApi.getDetachedMediaUploadUrl(fileName, contentType);
  const getUploadUrlMs = elapsedMs(getUploadStartedAt);

  const uploadStartedAt = Date.now();
  onUpdate(makeSnapshot('uploading', 0));
  const { etag } = await notesApi.uploadImagesToOss(
    presigned.upload_url,
    file,
    contentType,
    presigned.headers
  );
  const uploadOssMs = elapsedMs(uploadStartedAt);

  const confirmStartedAt = Date.now();
  onUpdate(makeSnapshot('confirming', 0));
  const confirmed = await notesApi.confirmDetachedMediaUpload({
    file_url: presigned.file_url,
    object_key: presigned.object_key,
    etag: etag ?? undefined,
    file_size: file.size,
    mime_type: contentType,
  });
  const confirmUploadMs = elapsedMs(confirmStartedAt);
  onUpdate(makeSnapshot('uploaded', 1));

  return {
    fileUrl: confirmed.file_url,
    objectKey: confirmed.object_key,
    etag: confirmed.etag,
    fileSize: confirmed.file_size,
    mimeType: confirmed.mime_type,
    metrics: {
      totalMs: elapsedMs(startedAt),
      getUploadUrlMs,
      uploadOssMs,
      confirmUploadMs,
    },
  };
}

export function normalizeMimeType(file: File): string {
  if (file.type) return file.type;
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  if (ext === 'png') return 'image/png';
  if (ext === 'gif') return 'image/gif';
  if (ext === 'webp') return 'image/webp';
  return 'image/jpeg';
}

export function isSupportedComposerImage(file: File): boolean {
  return normalizeMimeType(file).startsWith('image/');
}

function elapsedMs(startedAt: number): number {
  return Math.max(0, Date.now() - startedAt);
}

function makeSnapshot(
  stage: ComposerUploadStage,
  stageProgress: number
): ComposerUploadSnapshot {
  const clamped = Math.max(0, Math.min(1, stageProgress));
  if (stage === 'requesting_upload_url') {
    return { stage, stageProgress: clamped, overallProgress: 0.05 * clamped };
  }
  if (stage === 'uploading') {
    return { stage, stageProgress: clamped, overallProgress: 0.05 + 0.85 * clamped };
  }
  if (stage === 'confirming') {
    return { stage, stageProgress: clamped, overallProgress: 0.9 + 0.1 * clamped };
  }
  if (stage === 'uploaded') {
    return { stage, stageProgress: 1, overallProgress: 1 };
  }
  return { stage, stageProgress: clamped, overallProgress: clamped };
}
